import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import Avatar2D from "@/components/Avatar2D";
import { primeRaider } from "@/lib/raiderLooks";
import InductionSteps from "@/components/InductionSteps";
import {
  BUILDER, CHARACTERS, cleanLook, customCharacter, myCharacter, RESTRICTED, setLocalCharacter,
} from "@/lib/avatarConfig";

const TABS = [
  { id: "raiders", label: "Raiders" },
  { id: "body", label: "Body" },
  { id: "attire", label: "Attire" },
];

const sameLook = (a, b) => JSON.stringify(cleanLook(a)) === JSON.stringify(cleanLook(b));

// Pick your raider from the character list, or build your own: a body first,
// then what they wear and carry. /avatar?welcome=1 is the step right after
// registering, where skipping keeps the one we picked for you.
export default function AvatarEditor() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const welcome = params.get("welcome") === "1";
  const initial = useMemo(() => myCharacter(user), [user]);
  const [look, setLook] = useState(() => cleanLook(initial.look));
  const [tab, setTab] = useState(initial.id === "custom" ? "body" : "raiders");
  const [saving, setSaving] = useState(false);
  // Attire you picked yourself survives tapping a different raider: the
  // raider only swaps the body (and any attire you haven't touched).
  const [ownAttire, setOwnAttire] = useState(() => new Set());
  const [base, setBase] = useState(() => (initial.id === "custom" ? null : initial.id));
  const savedLook = useMemo(() => cleanLook(initial.look), [initial]);

  // A ready-made raider while the look still matches one, else your own.
  const premade = CHARACTERS.find((c) => sameLook(c.look, look)) || null;
  const picked = premade || customCharacter(look);
  const lockOf = (key, id) => RESTRICTED[key]?.[id];
  const canWear = (key, id) => !lockOf(key, id) || !!user?.[lockOf(key, id).flag];

  const dirty = !sameLook(look, savedLook);
  const attireKeys = useMemo(() => new Set(BUILDER.attire.map((f) => f.key)), []);
  const set = (key, value) => {
    if (attireKeys.has(key)) setOwnAttire((s) => new Set(s).add(key));
    setLook((l) => ({ ...l, [key]: value }));
  };
  const pickRaider = (c) => {
    setBase(c.id);
    setLook((l) => {
      const next = { ...cleanLook(c.look) };
      ownAttire.forEach((k) => { next[k] = l[k]; });
      return cleanLook(next);
    });
  };
  const useRaiderOutfit = () => {
    const c = CHARACTERS.find((x) => x.id === base);
    setOwnAttire(new Set());
    if (c) setLook(cleanLook(c.look));
  };
  const saveLabel = saving ? "Saving…" : welcome ? "Save raider and continue" : dirty ? "Save raider" : "Saved";
  // In the induction, the next step is the How to play video.
  const done = () => navigate(welcome ? "/welcome?step=video" : "/profile", { replace: welcome });

  const save = async () => {
    setSaving(true);
    const body = premade ? { character: premade.id } : { character: "custom", look };
    try {
      const { data } = await api.put("/me/avatar3d", body);
      setUser(data);
      primeRaider(data.id, data.avatar3d);
      if (premade) setLocalCharacter(user.id, premade.id);
      toast.success(`${picked.name} is ready to raid`);
      done();
    } catch (err) {
      const status = err?.response?.status;
      if (premade && (status === 404 || status === 405 || status === 422)) {
        // The server doesn't have characters yet (a preview build): keep it here.
        setLocalCharacter(user.id, premade.id);
        setUser((u) => ({ ...u }));
        toast.success(`${picked.name} saved on this device`);
        done();
      } else {
        toast.error(formatApiError(err?.response?.data?.detail) || err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const fields = tab === "body" ? BUILDER.body : BUILDER.attire;

  return (
    <main className="max-w-6xl mx-auto px-4 md:px-10 py-8 md:py-12" data-testid="avatar-editor">
      {welcome && <InductionSteps step={1} />}
      <div className="mb-6">
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">
          {welcome ? "Choose your raider" : "Your raider"}
        </h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          This is who crosses into enemy territory, breaks into their base and snatches the flag.
          Pick a ready-made raider, or build your own: choose a body, then dress them.
          {welcome ? " Or skip and keep the one we picked for you. You can change it any time from your profile." : ""}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,4fr)_minmax(0,7fr)] gap-6">
        <div className={`lg:sticky lg:top-20 self-start bg-surface border border-border rounded-2xl overflow-hidden ${tab === "raiders" ? "" : "hidden lg:block"}`}>
          <div className="relative bg-gradient-to-b from-brand/15 to-transparent pt-6 pb-2">
            <Avatar2D character={picked} pose="cheer" className="h-48 md:h-80 mx-auto aspect-[240/256]" />
          </div>
          <div className="px-5 pb-5 text-center">
            <div className="font-display text-2xl font-black tracking-tight" data-testid="picked-name">{picked.name}</div>
            <button
              type="button"
              onClick={save}
              disabled={saving || (!dirty && !welcome)}
              className="mt-4 w-full h-11 rounded-full bg-brand hover:bg-brand-hover text-brand-foreground font-semibold disabled:opacity-60"
              data-testid="avatar-save"
            >
              {saveLabel}
            </button>
            {dirty && <p className="mt-2 text-xs font-semibold text-brand" data-testid="unsaved-note">Unsaved changes</p>}
            <button
              type="button"
              onClick={() => { if (!dirty || welcome || window.confirm("Leave without saving your raider?")) done(); }}
              className="mt-2 w-full h-10 rounded-full text-sm font-semibold text-muted-foreground hover:text-foreground"
              data-testid="avatar-skip"
            >
              {welcome ? "Skip for now" : "Cancel"}
            </button>
          </div>
        </div>

        <div className="min-w-0">
          <div className="flex gap-1 p-1 mb-4 rounded-full bg-surface border border-border w-full sm:w-fit" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => setTab(t.id)}
                className={`flex-1 sm:flex-none h-9 px-5 rounded-full text-sm font-semibold transition-colors ${
                  tab === t.id ? "bg-brand text-brand-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`avatar-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab !== "raiders" && (
            // Phones: keep the raider in view while trying on parts.
            <div className="lg:hidden sticky top-16 z-20 mb-4 flex items-center gap-3 p-2 pr-3 rounded-2xl bg-surface/95 backdrop-blur border border-border" data-testid="builder-mini-preview">
              <Avatar2D character={picked} pose="idle" className="h-24 aspect-[240/256] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-display text-lg font-black tracking-tight truncate">
                  {picked.name}
                  {dirty && <span className="ml-2 text-[11px] font-bold uppercase text-brand align-middle">Unsaved</span>}
                </div>
                <button
                  type="button"
                  onClick={save}
                  disabled={saving || (!dirty && !welcome)}
                  data-testid="avatar-save-mini"
                  className="mt-1 h-9 px-4 rounded-full bg-brand hover:bg-brand-hover text-brand-foreground text-sm font-semibold disabled:opacity-60"
                >
                  {saveLabel}
                </button>
              </div>
            </div>
          )}

          {tab === "raiders" ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-3 content-start">
              <p className="col-span-full text-xs text-muted-foreground mb-1">
                Start from any raider, then change their body or attire.
                {ownAttire.size > 0 && " Attire you've picked stays on when you switch raider. "}
                {ownAttire.size > 0 && base && (
                  <button type="button" onClick={useRaiderOutfit} className="font-semibold text-brand underline" data-testid="use-raider-outfit">
                    Use this raider's own outfit
                  </button>
                )}
              </p>
              {CHARACTERS.map((c) => {
                const active = premade ? premade.id === c.id : base === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => pickRaider(c)}
                    className={`rounded-xl border-2 p-1.5 pt-3 transition-colors ${
                      active ? "border-brand bg-brand/10" : "border-border bg-surface hover:border-foreground/40"
                    }`}
                    data-testid={`character-${c.id}`}
                    aria-pressed={active}
                  >
                    <Avatar2D character={c} pose={active ? "taunt" : "idle"} className="h-24 md:h-28 mx-auto aspect-[240/256] pointer-events-none" />
                    <div className={`mt-1 text-[11px] md:text-xs font-bold truncate ${active ? "text-foreground" : "text-muted-foreground"}`}>{c.name}</div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="space-y-5">
              {fields.filter((f) => !f.when || f.when(look)).map((f) => (
                <section key={f.key} data-testid={`builder-${f.key}`}>
                  <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">{f.label}</h2>
                  {f.colours ? (
                    <div className="flex flex-wrap gap-2">
                      {f.colours.map((hex) => {
                        const on = (look[f.key] || "").toLowerCase() === hex.toLowerCase();
                        return (
                          <button
                            key={hex}
                            type="button"
                            onClick={() => set(f.key, hex)}
                            aria-label={`${f.label} ${hex}`}
                            aria-pressed={on}
                            className={`h-9 w-9 rounded-full border-2 transition-transform ${
                              on ? "border-brand scale-110 ring-2 ring-brand/40" : "border-border hover:scale-105"
                            }`}
                            style={{ background: hex }}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {f.options.filter((o) => canWear(f.key, o.id)).map((o) => {
                        const on = look[f.key] === o.id;
                        const lock = lockOf(f.key, o.id);
                        return (
                          <button
                            key={o.id}
                            type="button"
                            onClick={() => set(f.key, o.id)}
                            aria-pressed={on}
                            className={`h-9 px-4 rounded-full border text-sm font-semibold transition-colors ${
                              on ? "border-brand bg-brand/15 text-foreground" : "border-border bg-surface text-muted-foreground hover:text-foreground"
                            }`}
                            data-testid={`option-${f.key}-${o.id}`}
                          >
                            {o.label}
                            {lock && <span className="ml-1.5 text-[10px] font-bold uppercase text-brand">{lock.badge}</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
